import _ from 'underscore';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isMobile from 'ismobilejs';

import Conversation from './Conversation';

import Message from './Message';
import Introduction from './Introduction';
import TimestampHeader from './TimestampHeader';

import { setShouldScrollToBottom, setFetchingMoreMessages, setBlockChatInput } from '../actions/app-state';
import {
    fetchMoreMessages,
    setLastReceivedMessageId,
    resetUnreadCount,
    startPrechatCaptureConversation,
} from '../actions/conversation';
import { setIsAtBottom } from '../actions/ui';
import { WIDGET_STATE } from '../constants/app';
import { logo, logo2x } from '../constants/assets';
import { PRECHAT_CAPTURE_ERROR, PRECHAT_CAPTURE_MESSAGE_DELAY_MS } from '../constants/prechat-capture';
import { extractReplyActions } from '../reducers/conversations';
import { delegate } from '../utils/delegate';
import { getTop } from '../utils/dom';
import { getPrechatCaptureForm } from '../utils/prechat-capture';
import { SEND_STATUS, MESSAGE_TYPES } from '../constants/message';

const INTRO_BOTTOM_SPACER = 10;
const EXTRA_COMPONENT_BOTTOM_SPACER = 10;
const LOAD_MORE_LINK_HEIGHT = 47;
const AUTO_SCROLL_THRESHOLD_PIXELS = 60;
const DEFAULT_REPLY_ACTIONS_PARENT_MESSAGE = {};

export class ConversationContainerComponent extends Component {
    static propTypes = {
        dispatch: PropTypes.func.isRequired,
        embedded: PropTypes.bool.isRequired,
        shouldScrollToBottom: PropTypes.bool.isRequired,
        isFetchingMoreMessages: PropTypes.bool.isRequired,
        conversation: PropTypes.object.isRequired,
        hasMoreMessages: PropTypes.bool.isRequired,
        introHeight: PropTypes.number,
        connectNotificationTimestamp: PropTypes.number,
        errorNotificationMessage: PropTypes.string,
        config: PropTypes.object.isRequired,
        text: PropTypes.object.isRequired,
        typingIndicatorShown: PropTypes.bool.isRequired,
        typingIndicatorName: PropTypes.string,
        replyActionsOriginal: PropTypes.array.isRequired,
        replyActionsParentMessage: PropTypes.object.isRequired,
        isAtBottom: PropTypes.bool,
        unreadCount: PropTypes.number,
        user: PropTypes.object.isRequired,
        widgetOpened: PropTypes.bool.isRequired,
    };

    _container = React.createRef();
    _messagesContainer = React.createRef();

    _prechatCaptureMessages = [];

    constructor(props) {
        super(props);
        this.updateLastReceivedMessageId(props.conversation._id, props.conversation.messages);
    }

    onTouchMove = (e) => {
        // in embedded we need to let user scroll past the conversation
        if (!this.props.embedded) {
            const top = this._container.current.scrollTop;
            const totalScroll = this._container.current.scrollHeight;
            const currentScroll = top + this._container.current.offsetHeight;

            // this bit of code makes sure there's always something to scroll
            // in the conversation view so the page behind won't start scrolling
            // when hitting top or bottom.
            if (top === 0) {
                this._container.current.scrollTop = 1;
            } else if (currentScroll === totalScroll) {
                this._container.current.scrollTop = top - 1;
            }

            // On iOS devices, when the messages container is not scrollable,
            // selecting it will cause the background page to scroll.
            // In order to fix, prevent default scroll behavior.
            if (isMobile().apple.device && this._messagesContainer.current.offsetHeight > this._messages.offsetHeight) {
                e.preventDefault();
            }
        }
    };

    onScroll = () => {
        const { clientHeight, scrollHeight, scrollTop } = this._container.current;
        const distFromBottom = scrollHeight - scrollTop - clientHeight;
        const upward = this.lastScrollTop > scrollTop;
        this.lastScrollTop = scrollTop;

        if (upward) {
            // Auto scroll: Automatically scroll when receiving new messages
            // If the user scrolls upward to view older messages beyond AUTO_SCROLL_THRESHOLD_PIXELS
            // then we disable auto scroll so they don't lose their place
            if (this._autoScroll && distFromBottom > AUTO_SCROLL_THRESHOLD_PIXELS) {
                this._autoScroll = false;
            }

            const { hasMoreMessages, isFetchingMoreMessages } = this.props;
            if (scrollTop === 0 && hasMoreMessages && !isFetchingMoreMessages) {
                this.fetchHistory();
            }
        } else if (!this._autoScroll && distFromBottom <= AUTO_SCROLL_THRESHOLD_PIXELS) {
            this._autoScroll = true;
        }

        this.updateScrollPosition(this.props.isAtBottom, this.props.unreadCount);
    };

    fetchHistory = () => {
        const {
            conversation: { messages },
            dispatch,
        } = this.props;

        // make sure the last message is one from the server, otherwise it doesn't need to scroll to previous first message
        if (messages.length > 0 && messages[messages.length - 1]._id) {
            this._lastTopMessageId = messages[0]._id;
        }

        const top = getTop(this._topMessageNode, this._container.current);
        this._lastTopMessageNodePosition = top - this._container.current.scrollTop;
        dispatch(setFetchingMoreMessages(true));

        // Timeout is needed because we need to compute sizes of HTML elements and thus need to make sure everything has rendered
        setTimeout(() => {
            dispatch(fetchMoreMessages());
        }, 400);
    };

    onMessageLoad = () => {
        // Auto scroll for messages that have a loading state, such as image messages
        if (this._autoScroll) {
            this.scrollToBottom();
        }
    };

    getScrollTopAtBottom = () => {
        const logo = this._logo;
        const logoHeight = logo ? logo.clientHeight : 0;
        const scrollTopAtBottom =
            this._container.current.scrollHeight -
            this._container.current.clientHeight -
            logoHeight -
            INTRO_BOTTOM_SPACER;
        return { scrollTopAtBottom, container: this._container.current };
    };

    scrollToBottom = () => {
        const { dispatch, shouldScrollToBottom, typingIndicatorShown } = this.props;
        const { container, scrollTopAtBottom } = this.getScrollTopAtBottom();
        let newScrollTop = scrollTopAtBottom;
        const replyActions = extractReplyActions(this.props.replyActionsParentMessage);

        if (replyActions.length > 0 || typingIndicatorShown) {
            newScrollTop = newScrollTop + EXTRA_COMPONENT_BOTTOM_SPACER;
        }

        container.scrollTop = newScrollTop;

        if (shouldScrollToBottom) {
            dispatch(setShouldScrollToBottom(false));
        }
    };

    updateScrollPosition = (currentIsAtBottom, unreadCount) => {
        const { container, scrollTopAtBottom } = this.getScrollTopAtBottom();
        const isAtBottom = scrollTopAtBottom <= container.scrollTop + 1;

        if (currentIsAtBottom === isAtBottom) {
            return;
        }

        this.props.dispatch(setIsAtBottom(isAtBottom));

        const widgetIsOpen = this.props.widgetOpened || this.props.embedded;
        if (widgetIsOpen && unreadCount > 0 && isAtBottom) {
            this.props.dispatch(resetUnreadCount());
        }
    };

    scrollToPreviousFirstMessage = () => {
        const node = this._lastTopMessageNode;
        // This will scroll to specified node if we've reached the oldest messages.
        // Otherwise, scroll to this._lastTopMessageNode
        if (!this.props.hasMoreMessages) {
            this._container.current.scrollTop = getTop(node, this._container.current) - LOAD_MORE_LINK_HEIGHT;
        } else {
            if (this._lastTopMessageNodePosition && !this._isScrolling) {
                this._isScrolling = true;

                // When fetching more messages, we want to make sure that after
                // render, the messages stay in the same places
                this._container.current.scrollTop =
                    getTop(node, this._container.current) - this._lastTopMessageNodePosition;
                this._isScrolling = false;
            }
        }
        this._lastTopMessageNode = undefined;
    };

    componentDidMount() {
        // On component render, force scroll to bottom, or else conversation will
        // find itself at a random spot
        this._autoScroll = true;
        this.scrollToBottom();
    }

    UNSAFE_componentWillUpdate(nextProps) {
        const currentMessages = this.props.conversation.messages;
        const { isFetchingMoreMessages, typingIndicatorShown: currentTypingIndicatorShown } = this.props;
        const newMessages = nextProps.conversation.messages;
        const {
            typingIndicatorShown: newTypingIndicatorShown,
            isAtBottom: newIsAtBottom,
            unreadCount: newUnreadCount,
            shouldScrollToBottom,
        } = nextProps;
        const receivedNewMessage = newMessages.length !== currentMessages.length;
        const typingIndicatorToggled = currentTypingIndicatorShown !== newTypingIndicatorShown;

        if (receivedNewMessage) {
            // The message Id should be updated on every update
            this.updateLastReceivedMessageId(this.props.conversation._id, newMessages);
        }

        if (isFetchingMoreMessages) {
            // Do not auto scroll if we're in the middle of fetching new messages
            return;
        }

        // Some conversation actions may force scrolling behavior via the shouldScrollToBottom prop
        if (shouldScrollToBottom) {
            this._shouldScroll = true;
            this.props.dispatch(setShouldScrollToBottom(false));
            return;
        }

        if (!this._lastNode) {
            this._shouldScroll = true;
            return;
        }

        const currentReplyActions = extractReplyActions(this.props.replyActionsParentMessage);
        const newReplyActions = extractReplyActions(nextProps.replyActionsParentMessage);

        // Array refs do not match so we can't just compare currentReplyActions === newReplyActions
        if (
            newReplyActions.length > 0 &&
            (currentReplyActions.length !== newReplyActions.length ||
                currentReplyActions[0]._id !== newReplyActions[0]._id)
        ) {
            this._shouldScroll = true;
            return;
        }

        if (receivedNewMessage || typingIndicatorToggled) {
            // Auto scroll for typing indicator and new messages, if auto scroll is active
            this._shouldScroll = this._autoScroll;
        }
        this.updateScrollPosition(newIsAtBottom, newUnreadCount);
    }

    componentDidUpdate() {
        const {
            config: { prechatCapture = {} },
            embedded,
            isFetchingMoreMessages,
            widgetOpened,
        } = this.props;
        const { messages } = this.props.conversation;
        const conversationHasMessages = messages.length > 0;
        const isChatVisible = widgetOpened || embedded;

        if (prechatCapture.enabled && isChatVisible) {
            this.updatePrechatCaptureMessages();

            if (this.isPrechatCaptureSubmissionPending && conversationHasMessages) {
                this.isPrechatCaptureSubmissionPending = false;
            }
        }

        if (isFetchingMoreMessages) {
            this.scrollToPreviousFirstMessage();
        } else if (this._shouldScroll) {
            // Have to workaround by set timeout, somehow doesn't get called the second time.
            setTimeout(() => {
                this.scrollToBottom();
                this._shouldScroll = false;
            }, 0);
        }
    }

    generateLogo() {
        // Bookboost: add always-true condition to render bookboost logo
        if (!IS_BRANDED || true) {

            const logoStyle = {
                paddingBottom: 14,
            };

            return (
                <div className="logo" ref={(c) => (this._logo = c)} style={logoStyle}>
                	{ /* Bookboost: changed a[href] and img[alt] */ }
                    <a
                        href="http://bookboost.io/"
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        <img
                            className="image"
                            style={{ paddingBottom: '3px' }}
                            src={logo}
                            srcSet={`${logo} 1x, ${logo2x} 2x`}
                            alt="Bookboost"
                        />
                    </a>
                </div>
            );
        }
    }

    updateLastReceivedMessageId(conversationId, messages) {
        if (messages.length) {
            this.props.dispatch(setLastReceivedMessageId(conversationId, messages[messages.length - 1]._id));
        }
    }

    addOrReplacePrechatCaptureMessage = (message, predicate) => {
        const { dispatch } = this.props;
        const index = predicate && this._prechatCaptureMessages.findIndex(predicate);

        if (Number.isInteger(index) && index >= 0) {
            this._prechatCaptureMessages[index] = message;
            this._prechatCaptureMessages = [...this._prechatCaptureMessages];
        } else {
            this._prechatCaptureMessages = [...this._prechatCaptureMessages, message];
        }

        dispatch(setShouldScrollToBottom(true));
    };

    upsertPrechatCaptureFormResponseMessage = (formResponse, status) => {
        const received = Date.now() / 1000;
        const formResponseMessageProps = {
            ...formResponse,
            received,
            messageRef: (c) => (this._lastNode = c),
            key: `prechat-capture-form-response-${received}`,
            onLoad: this.onMessageLoad,
            showStatusIndicator: false,
            sendStatus: status,
        };
        const formResponseMessage = {
            component: <Message {...formResponseMessageProps} />,
        };
        const formResponseMessagePredicate = (m) => m.component.props.type === MESSAGE_TYPES.FORM_RESPONSE;
        this.addOrReplacePrechatCaptureMessage(formResponseMessage, formResponseMessagePredicate);
    };

    submitPrechatCapture = (formResponse) => {
        const {
            text: { prechatCaptureConfirmationText },
            dispatch,
        } = this.props;

        this.upsertPrechatCaptureFormResponseMessage(formResponse, SEND_STATUS.SENDING);

        const confirmationMessage = {
            type: 'text',
            text: prechatCaptureConfirmationText,
            role: 'appMaker',
            received: Date.now() / 1000,
        };

        const messages = this._prechatCaptureMessages
            .filter((message) => message.component.props.type)
            .map((message) => message.component.props);

        messages.push(confirmationMessage);

        this.isPrechatCaptureSubmissionPending = true;

        const delegatedFormResponse = delegate.beforeSend(formResponse, this.props.conversation);
        return dispatch(startPrechatCaptureConversation(messages, delegatedFormResponse))
            .then(() => {
                this.upsertPrechatCaptureFormResponseMessage(delegatedFormResponse, SEND_STATUS.SENT);
            })
            .catch((err) => {
                if (err && err.message === PRECHAT_CAPTURE_ERROR) {
                    this.upsertPrechatCaptureFormResponseMessage(delegatedFormResponse, SEND_STATUS.FAILED);
                }

                throw err;
            });
    };

    updatePrechatCaptureMessages() {
        const { dispatch, user } = this.props;
        const hasUser = user.signedUpAt;
        const hasPrechatCaptureBeenInitiated = this._prechatCaptureMessages.length > 0;

        if (!hasUser && !hasPrechatCaptureBeenInitiated) {
            this._generatePrechatCaptureMessages();

            dispatch(setBlockChatInput(true));
        } else if (hasUser && hasPrechatCaptureBeenInitiated && !this.isPrechatCaptureSubmissionPending) {
            this._prechatCaptureMessages = [];

            dispatch(setBlockChatInput(false));
        }
    }

    _generatePrechatCaptureMessages() {
        const { config, text } = this.props;

        const msg = {
            type: 'text',
            text: text.prechatCaptureGreetingText,
            role: 'appMaker',
            received: Date.now() / 1000,
        };

        const form = getPrechatCaptureForm(config.prechatCapture, text, this.submitPrechatCapture);

        this._prechatCaptureMessages = [
            ...this._prechatCaptureMessages,
            {
                component: <TimestampHeader value={msg.received} key={`ts-header-${msg.received}`} />,
            },
        ];

        const refCallback = (c) => {
            this._lastNode = c;
        };

        const messageProps = {
            firstInGroup: true,
            hasFormAfter: true,
            messageRef: refCallback,
            key: `prechat-capture-intro-${msg.received}`,
            onLoad: this.onMessageLoad,
            showStatusIndicator: false,
        };

        const formMessageProps = {
            messageRef: refCallback,
            key: `prechat-capture-form-${form.received}`,
            onLoad: this.onMessageLoad,
            showStatusIndicator: false,
        };

        const greetUser = () =>
            new Promise((resolve, reject) => {
                setTimeout(() => {
                    const greetingMessage = {
                        component: <Message {...messageProps} {...msg} />,
                    };

                    try {
                        this.addOrReplacePrechatCaptureMessage(greetingMessage);
                    } catch (e) {
                        reject(e);
                    }

                    resolve();
                }, 1000);
            });

        const presentUserWithForm = () =>
            new Promise((resolve, reject) => {
                setTimeout(() => {
                    const formMessage = {
                        component: <Message {...formMessageProps} {...form} />,
                    };

                    try {
                        this.addOrReplacePrechatCaptureMessage(formMessage);
                    } catch (e) {
                        reject(e);
                    }

                    resolve();
                }, PRECHAT_CAPTURE_MESSAGE_DELAY_MS);
            });

        greetUser().then(presentUserWithForm);
    }

    getMessagesRef = (el) => {
        this._messages = el;
    };

    render() {
        const {
            config,
            connectNotificationTimestamp,
            errorNotificationMessage,
            hasMoreMessages,
            introHeight,
            isFetchingMoreMessages,
            replyActionsOriginal,
            replyActionsParentMessage,
            text,
            typingIndicatorName,
            typingIndicatorShown,
        } = this.props;

        const { fetchingHistory, fetchHistory } = text;
        const { backgroundImageUrl } = config;

        let retrieveHistory;
        if (hasMoreMessages) {
            if (isFetchingMoreMessages) {
                retrieveHistory = <div className="fetch-history">{fetchingHistory}</div>;
            } else {
                const onClick = (e) => {
                    e.preventDefault();
                    this.fetchHistory();
                };

                retrieveHistory = (
                    <div className="fetch-history">
                        <a href="#" onClick={onClick}>
                            {fetchHistory}
                        </a>
                    </div>
                );
            }
        }

        const classes = [];
        // show the introduction pane as part of the conversation if it is not fixed
        const showIntro = !config.fixedIntroPane && !hasMoreMessages;

        if (!showIntro) {
            classes.push('no-intro');
        }

        if (errorNotificationMessage) {
            classes.push('notification-shown');
        }

        const conversationStyle = {};

        if (backgroundImageUrl) {
            conversationStyle.backgroundImage = `url(${backgroundImageUrl})`;
        }

        const messagesContainerStyle = {
            maxHeight: showIntro ? `calc(100% - ${introHeight + INTRO_BOTTOM_SPACER}px)` : '100%',
        };

        return (
            <div
                id="conversation"
                className={classes.join(' ')}
                ref={this._container}
                onTouchMove={this.onTouchMove}
                onScroll={this.onScroll}
                style={conversationStyle}
            >
                {showIntro ? <Introduction /> : null}
                <div ref={this._messagesContainer} className="messages-container" style={messagesContainerStyle}>
                    {retrieveHistory}
                    <Conversation
                        conversation={this.props.conversation}
                        replyActionsParentMessage={replyActionsParentMessage}
                        replyActionsOriginal={replyActionsOriginal}
                        typingIndicatorShown={typingIndicatorShown}
                        typingIndicatorName={typingIndicatorName}
                        connectNotificationTimestamp={connectNotificationTimestamp}
                        text={text}
                        config={config}
                        prechatCaptureMessages={this._prechatCaptureMessages}
                        getMessagesRef={this.getMessagesRef}
                        getlastTopMessageNodeRef={(el) => (this._lastTopMessageNode = el)}
                        getLastNodeRef={(el) => (this._lastNode = el)}
                        getTopMessageNodeRef={(el) => (this._topMessageNode = el)}
                        onMessageLoad={this.onMessageLoad}
                        lastTopMessageId={this._lastTopMessageId}
                    />
                    {this.generateLogo()}
                </div>
            </div>
        );
    }
}

export default connect(
    ({ appState, conversations, ui: { text, isAtBottom }, user, config }) => {
        const conversation = conversations[appState.activeConversationId];

        const replyActionsMessage = conversation.replyActions.message;
        let replyActionsParentMessage;

        if (replyActionsMessage) {
            replyActionsParentMessage = delegate.beforeDisplay(replyActionsMessage, conversation) || {
                _isDeletedByBeforeDisplayDelegate: true,
            };

            if (replyActionsParentMessage._isDeletedByBeforeDisplayDelegate) {
                replyActionsParentMessage._id = replyActionsMessage._id;
            }
        } else {
            replyActionsParentMessage = DEFAULT_REPLY_ACTIONS_PARENT_MESSAGE;
        }

        const typingIndicatorShown = !!appState.typingEvents.length;
        // R1 multi-party requires to show only single typing indicator, we select only the first one.
        const typingIndicatorName = !typingIndicatorShown ? null : _.first(appState.typingEvents).name;

        return {
            conversation,
            replyActionsOriginal: conversation.replyActions.actions,
            replyActionsParentMessage,
            embedded: appState.widgetState === WIDGET_STATE.EMBEDDED,
            widgetOpened: appState.widgetState === WIDGET_STATE.OPENED,
            shouldScrollToBottom: appState.shouldScrollToBottom,
            isFetchingMoreMessages: appState.isFetchingMoreMessages,
            hasMoreMessages: conversation.hasMoreMessages,
            introHeight: appState.introHeight,
            connectNotificationTimestamp: conversation.isDefault ? appState.connectNotificationTimestamp : 0,
            errorNotificationMessage: appState.errorNotificationMessage,
            unreadCount: conversation.unreadCount,
            config,
            text,
            isAtBottom,
            typingIndicatorShown,
            typingIndicatorName,
            user,
        };
    },
    undefined,
    undefined,
    {
        forwardRef: true,
    },
)(ConversationContainerComponent);
